import { defineComponent as _defineComponent } from 'vue'
import { PropType, ref, watch, computed } from 'vue';
import BaseBottomSheet from '@/views/base/BaseBottomSheet.vue';
import { Sds } from '@/interfaces/model/general/sds';
import { UpdateCompanySdsesRequestBody } from '@/interfaces/api/general/company';
import BulkDeleteDialog from '@/views/dashboard/bottomSheet/BulkDeleteDialog.vue';
import BulkUpdateDialog from '@/views/dashboard/bottomSheet/BulkUpdateDialog.vue';
import BulkDownloadDialog from '@/views/dashboard/bottomSheet/BulkDownloadDialog.vue';
import SdsBulkOperationsDialog from '@/views/dashboard/bottomSheet/SdsBulkOperationsDialog.vue';
import RiskReassessmentCsvUploadDialog from '@/views/dashboard/bottomSheet/RiskReassessmentCsvUploadDialog.vue';
import { usePermissionComposable } from '@/composables/usePermission';
import { defineMessages } from '@/lib/i18n';
import sharedMessages from '@/lib/shared-messages';
import { csvItem } from '@/utils/csvValidation';
import { StatusId } from '@/lib/enum';
import { isTrialEnvironment } from '@/env';
import { useCompanyFeatureComposable } from '@/composables/useCompanyFeatures';


export default /*#__PURE__*/_defineComponent({
  __name: 'SdsBottomSheet',
  props: {
  total: {
    type: Number,
    default: 0,
  },
  totalChangedNumber: {
    type: Number,
    default: 0,
  },
  fetchSdsesProcessString: {
    type: String,
    default: '',
  },
  uploadProcessNumber: {
    type: Number,
    default: 0,
  },
  pdfDownloadCount: {
    type: Number,
    default: 0,
  },
  pdfMonthlyLimit: {
    type: Number,
    default: 0,
  },
  isSelectedAllSdses: {
    type: Boolean,
    default: false,
    required: false,
  },
  isSelectedWholeSdses: {
    type: Boolean,
    default: false,
    required: false,
  },
  selectedSdses: {
    type: Array as PropType<Sds[]>,
    default: () => [],
  },
  isEditable: {
    type: Boolean,
    default: false,
    required: false,
  },
  isDownloadable: {
    type: Boolean,
    default: false,
    required: false,
  },
  isUploadable: {
    type: Boolean,
    default: false,
    required: false,
  },
  isLoadingDelete: {
    type: Boolean,
    default: false,
    required: false,
  },
  isLoadingUpdate: {
    type: Boolean,
    default: false,
    required: false,
  },
  isOperationVisible: {
    type: Boolean,
  },
  isDisableSdsBulkCsvDialog: {
    type: Boolean,
    default: false,
    required: false,
  },
  isDisableSdsRiskReassessmentDialog: {
    type: Boolean,
    default: false,
    required: false,
  },
  toggleWholeSelectedSdses: {
    type: Function as PropType<(check: boolean) => void>,
    required: false,
    default: (check) => check,
  },
  resetSelectedSdses: {
    type: Function as PropType<(check: boolean) => void>,
    required: false,
    default: (check) => check,
  },
},
  emits: ["downloadPDF", "download", "delete", "update", "downloadCSV", "csvFileValidate", "downloadRiskReassessmentCSV", "riskReassessmentCsvFileUpload"],
  setup(__props, { emit: emits }: { emit: ({
  (e: 'downloadPDF'): void;
  (e: 'download'): void;
  (e: 'delete'): void;
  (
    e: 'update',
    form: Omit<UpdateCompanySdsesRequestBody, 'sds_uuid_list'>
  ): void;
  (e: 'downloadCSV'): void;
  (
    e: 'csvFileValidate',
    csvDataArray: csvItem[],
    hideParentModal: boolean
  ): void;
  (e: 'downloadRiskReassessmentCSV'): void;
  (
    e: 'riskReassessmentCsvFileUpload',
    file: File,
    hideParentModal: boolean
  ): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props;

const m = defineMessages({
  selectedAllSdsInList: 'SDS一覧内の{total}件のすべてが選択されています。',
  clearSelection: '選択をクリア',
  selectedAllSdsOnPage:
    'このページ内のSDS{count}件のすべてが選択されています。',
  selectedCount: '{count}件選択中',
  fileDelete: 'ファイル削除',
  allSelectSDS: '全SDS{total}件をすべて選択',
  bulkPDFDownload: 'SDSダウンロード',
  cannoDeleteSdsesFromSdsStatus: 'データ化不可以外のSDSが選択されています',
  cannoDeleteSdsesFromPermission: '削除権限を持っていません',
});





const { hasPermission, PERMISSIONS: P } = usePermissionComposable();
const { isFeatureActive, COMPANY_FEATURES } = useCompanyFeatureComposable();

const isRiskAssessmentActive = computed(() =>
  isFeatureActive(COMPANY_FEATURES.CHEMICAN_RISK_ASSESSMENT)
);

const canDeleteSelectedSdses = computed(() => {
  return (
    !isTrialEnvironment ||
    props.selectedSdses.every(
      (sds) => StatusId.fromStatusId(sds.status_id) === StatusId.ERROR
    )
  );
});

const download = (): void => {
  emits('download');
};

const deleteSelectedSdses = (): void => {
  emits('delete');
};

const downloadSelectedSdsesCSV = (): void => {
  emits('downloadCSV');
};

const csvFileValidate = (
  csvDataArray: csvItem[],
  hideParentModal: boolean
): void => {
  emits('csvFileValidate', csvDataArray, hideParentModal);
};

const downloadSelectedRiskReassessmentCSV = (): void => {
  emits('downloadRiskReassessmentCSV');
};

const riskReassessmentCsvFileUpload = (
  file: File,
  hideParentModal: boolean
): void => {
  emits('riskReassessmentCsvFileUpload', file, hideParentModal);
};

const toggleVisible = () => {
  isVisible.value = !isVisible.value;
};

const updateSelectedSdses = (
  form: Omit<UpdateCompanySdsesRequestBody, 'sds_uuid_list'>
): void => {
  emits('update', form);
};

const downloadSelectedSdsesPDF = () => {
  emits('downloadPDF');
};

const isDeleteDialogVisible = ref(false);
const isBulkUpdateDialogVisible = ref(false);
const isCsvUploadDownloadDialogVisible = ref(false);
const isRiskReassessmentDialogVisible = ref(false);
const isDownloadDialogVisible = ref(false);
const isVisible = ref(false);

watch(
  () => props.selectedSdses.length,
  (newLength) => {
    if (newLength === 0) {
      isVisible.value = false;
    }
  }
);

watch(
  () => props.isDisableSdsBulkCsvDialog,
  (newVal) => {
    isCsvUploadDownloadDialogVisible.value = newVal;
  }
);

watch(
  () => props.isDisableSdsRiskReassessmentDialog,
  (newVal) => {
    isRiskReassessmentDialogVisible.value = newVal;
  }
);

return { __sfc: true,m, props, emits, hasPermission, P, isFeatureActive, COMPANY_FEATURES, isRiskAssessmentActive, canDeleteSelectedSdses, download, deleteSelectedSdses, downloadSelectedSdsesCSV, csvFileValidate, downloadSelectedRiskReassessmentCSV, riskReassessmentCsvFileUpload, toggleVisible, updateSelectedSdses, downloadSelectedSdsesPDF, isDeleteDialogVisible, isBulkUpdateDialogVisible, isCsvUploadDownloadDialogVisible, isRiskReassessmentDialogVisible, isDownloadDialogVisible, isVisible, BaseBottomSheet, BulkDeleteDialog, BulkUpdateDialog, BulkDownloadDialog, SdsBulkOperationsDialog, RiskReassessmentCsvUploadDialog, sharedMessages }
}

})